import AdCampaignsPage from "./AdCampaignsPage";
import RevenueDetailPage from "./RevenueDetailPage";
import FlashDealsPage from "./FlashDealsPage";
import { useState, useEffect, useCallback } from "react";
import MerchantDisputesPage from "./MerchantDisputesPage";
import OpsDisputeConsolePage from "./OpsDisputeConsolePage";
import AdminAlertConsolePage from "./AdminAlertConsolePage";
import AdDashboardPage from "./AdDashboardPage";
import FleetDashboard from "./FleetDashboard";
// EventConsolePage / SystemHealthPage are hidden from venue accounts; they return when account-scoped endpoints exist.
import PilotStructurePage from "./PilotStructurePage";
import RevenueDashboardPage from "./RevenueDashboardPage";
import VenueCreativesPage from "./VenueCreativesPage";
import DeviceOpsPage from "./DeviceOpsPage";
import LineBindingPage from "./LineBindingPage";
import ProvisioningPage from "./ProvisioningPage";
import DeviceFleetPage from "./DeviceFleetPage";
import QueueConsolePage from "./QueueConsolePage";
import DevicesPage from "./DevicesPage";

// ============================================================================
// API Configuration — Now points to unified /api/account
// ============================================================================
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import MarketingPage from "./MarketingPage";
import NotificationsPage from "./NotificationsPage";
import OrdersPage from "./OrdersPage";
import { formatCount, formatDeviceStats, formatMoney, UNAVAILABLE } from "./dashboardDeviceStats";
const API_BASE = "https://api.briolabs.io/api/account";

async function api(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
    body: options.body
      ? isFormData ? options.body : JSON.stringify(options.body)
      : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
}

// ============================================================================
// Theme / Styles (preserved from v1 — proven dark theme)
// ============================================================================
const theme = {
  bg: "#0F1117",
  card: "#1A1D27",
  cardHover: "#22262F",
  border: "#2A2E3A",
  accent: "#6C5CE7",
  accentLight: "#A29BFE",
  success: "#00B894",
  warning: "#FDCB6E",
  danger: "#E17055",
  text: "#E8E8ED",
  textMuted: "#8B8FA3",
  textDim: "#5A5E72",
  bronze: "#CD7F32",
  silver: "#C0C0C0",
  gold: "#FFD700",
  diamond: "#B9F2FF",
};

const baseStyles = {
  page: { minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: "'DM Sans', 'Noto Sans TC', sans-serif" },
  container: { maxWidth: 1200, margin: "0 auto", padding: "0 24px" },
  card: { background: theme.card, borderRadius: 16, border: `1px solid rgba(255,255,255,0.06)`, padding: 24, marginBottom: 16 },
  input: { width: "100%", padding: "12px 16px", background: "#141720", border: `1px solid ${theme.border}`, borderRadius: 10, color: theme.text, fontSize: 14, outline: "none", boxSizing: "border-box" },
  btn: { padding: "12px 24px", borderRadius: 10, border: "none", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "all 0.2s" },
  btnPrimary: { background: theme.accent, color: "#fff" },
  btnOutline: { background: "transparent", color: theme.accent, border: `1px solid ${theme.accent}` },
  label: { fontSize: 13, color: theme.textMuted, marginBottom: 6, display: "block", fontWeight: 500 },
};

// ============================================================================
// Capability Definitions
// ============================================================================
const CAPABILITY_META = {
  SCREEN_NETWORK: { label: "螢幕聯播網", icon: "📺", description: "綁定設備、排程內容、賺取廣告分潤" },
  FLASH_BUY: { label: "快閃購交易", icon: "🛒", description: "上架商品、管理庫存、接受訂單" },
  AD_BUYER: { label: "廣告投放", icon: "📢", description: "建立廣告活動、鎖定目標受眾" },
  PARTNER: { label: "經銷夥伴", icon: "🔗", description: "產生配對代碼、賺取安裝佣金" },
};

// Partner tier info (used when PARTNER capability is active)
const TIERS = {
  bronze: { name: "銅牌", emoji: "🥉", color: theme.bronze, share: "5%", bonus: "NT$300" },
  silver: { name: "銀牌", emoji: "🥈", color: theme.silver, share: "8%", bonus: "NT$400" },
  gold: { name: "金牌", emoji: "🥇", color: theme.gold, share: "10%", bonus: "NT$500" },
  diamond: { name: "鑽石", emoji: "💎", color: theme.diamond, share: "12%", bonus: "NT$500" },
};

// ============================================================================
// Helpers
// ============================================================================
function hasCap(account, type) {
  return account?.capabilities?.some(c => c.type === type && c.status === "active");
}
function getCapConfig(account, type) {
  const cap = account?.capabilities?.find(c => c.type === type && c.status === "active");
  return cap?.config || {};
}

// ============================================================================
// Toast
// ============================================================================
function Toast({ message, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{ position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)", background: "#2A2035", border: `1px solid ${theme.accent}`, borderRadius: 12, padding: "14px 24px", color: theme.text, fontSize: 14, zIndex: 9999, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", display: "flex", alignItems: "center", gap: 10, animation: "toastIn 0.3s ease" }}>
      <span style={{ fontSize: 20 }}>💡</span>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", color: theme.textMuted, cursor: "pointer", fontSize: 18, marginLeft: 8 }}>✕</button>
      <style>{`@keyframes toastIn { from { opacity: 0; transform: translateX(-50%) translateY(20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }`}</style>
    </div>
  );
}

// ============================================================================
// Loading
// ============================================================================
function Loading() {
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 80 }}>
      <div style={{ width: 32, height: 32, border: `3px solid ${theme.border}`, borderTop: `3px solid ${theme.accent}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ============================================================================
// StatCard
// ============================================================================
function StatCard({ label, value, sub, color, icon }) {
  return (
    <div style={{ ...baseStyles.card, flex: "1 1 200px", minWidth: 160 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 8 }}>{label}</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: (value === 0 || value === "0" || value === "NT$0") ? theme.textDim : (color || theme.text) }}>{value}</div>
          {sub && <div style={{ fontSize: 12, color: theme.textDim, marginTop: 4 }}>{sub}</div>}
        </div>
        {icon && <div style={{ fontSize: 28, opacity: 0.5 }}>{icon}</div>}
      </div>
    </div>
  );
}

// ============================================================================
// Login Page — Now creates a Business Account
// ============================================================================
function LoginPage({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", display_name: "", contact_name: "", phone: "" });
  const [consent, setConsent] = useState({ terms: false, location: false, marketing: false });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const body = isRegister
        ? {
            email: form.email,
            password: form.password,
            display_name: form.display_name,
            contact_name: form.contact_name,
            phone: form.phone,
            consent_terms_version: "v1.3",
            consent_location: consent.location,
            consent_marketing: consent.marketing,
          }
        : { email: form.email, password: form.password };
      const data = await api(endpoint, { method: "POST", body });
      localStorage.setItem("biz_token", data.data.accessToken);
      localStorage.setItem("biz_account", JSON.stringify(data.data.account));
      onLogin(data.data.account);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...baseStyles.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Noto+Sans+TC:wght@400;500;700&display=swap" rel="stylesheet" />
      <div style={{ width: "100%", maxWidth: 420, padding: 24 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <img src="/briolabs-mark.svg" alt="briolabs" style={{ width: 44, height: 44, objectFit: "contain", display: "block", margin: "0 auto 12px" }} />
          <div style={{ fontSize: 42, fontWeight: 700, background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentLight})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", marginBottom: 8 }}>
            brioCAST
          </div>
          <div style={{ fontSize: 16, color: theme.textMuted, letterSpacing: 2 }}>BUSINESS PORTAL</div>
        </div>

        <div style={{ ...baseStyles.card, padding: 32 }}>
          <h2 style={{ margin: "0 0 24px", fontSize: 20, fontWeight: 600 }}>
            {isRegister ? "商業帳戶註冊" : "商業帳戶登入"}
          </h2>

          {error && (
            <div style={{ background: "#2D1B1B", border: "1px solid #E17055", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: theme.danger }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 16 }}>
              <label style={baseStyles.label}>Email</label>
              <input style={baseStyles.input} type="email" placeholder="you@company.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={baseStyles.label}>密碼</label>
              <input style={baseStyles.input} type="password" placeholder="••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            </div>

            {isRegister && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <label style={baseStyles.label}>公司 / 商號名稱 *</label>
                  <input style={baseStyles.input} placeholder="大明診所" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} required />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={baseStyles.label}>聯絡人姓名</label>
                  <input style={baseStyles.input} placeholder="王大明" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={baseStyles.label}>手機 *</label>
                  <input style={baseStyles.input} placeholder="0912345678" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                </div>
              </>
            )}

            {isRegister && (
              <div style={{ marginTop: 16, marginBottom: 8, padding: "14px 16px", background: "#14161E", borderRadius: 10, border: `1px solid ${theme.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.textMuted, marginBottom: 12 }}>使用者同意事項</div>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: 12, color: theme.textMuted, marginBottom: 12 }}>
                  <input type="checkbox" checked={consent.terms} onChange={(e) => setConsent({ ...consent, terms: e.target.checked })} style={{ marginTop: 2, accentColor: theme.accent, flexShrink: 0 }} />
                  <span><span style={{ color: theme.danger, fontWeight: 700 }}>*</span> 我已閱讀並同意 <a href="https://legal.briolabs.io" target="_blank" rel="noopener noreferrer" style={{ color: theme.accent, textDecoration: "none" }}>《商業帳戶服務條款》</a>、<a href="https://legal.briolabs.io" target="_blank" rel="noopener noreferrer" style={{ color: theme.accent, textDecoration: "none" }}>《隱私權政策》</a>及<a href="https://legal.briolabs.io#sec12" target="_blank" rel="noopener noreferrer" style={{ color: theme.accent, textDecoration: "none" }}>《數據治理政策》</a>，並確認我已年滿 20 歲。</span>
                </label>
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: 12, color: theme.textMuted, marginBottom: 4 }}>
                  <input type="checkbox" checked={consent.location} onChange={(e) => setConsent({ ...consent, location: e.target.checked })} style={{ marginTop: 2, accentColor: theme.accent, flexShrink: 0 }} />
                  <div>
                    <span><span style={{ color: theme.danger, fontWeight: 700 }}>*</span> 我同意授權<a href="https://legal.briolabs.io#sec2" target="_blank" rel="noopener noreferrer" style={{ color: theme.accent, textDecoration: "none" }}>精確位置驗證</a>，以確保廣告曝光真實性並<strong style={{ color: theme.success }}>啟用收益變現功能</strong>。</span>
                    <div style={{ fontSize: 11, color: theme.textDim, marginTop: 3 }}>若不同意，您將無法賺取廣告分潤收益。</div>
                  </div>
                </label>
                <div style={{ borderTop: `1px solid ${theme.border}`, margin: "10px 0", paddingTop: 10 }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: 12, color: theme.textMuted }}>
                    <input type="checkbox" checked={consent.marketing} onChange={(e) => setConsent({ ...consent, marketing: e.target.checked })} style={{ marginTop: 2, accentColor: theme.accent, flexShrink: 0 }} />
                    <span>訂閱 brioCAST 商業夥伴電子報（含最新廣告成效優化技巧與獎勵活動通知）。<span style={{ color: theme.textDim }}>（選填）</span></span>
                  </label>
                </div>
              </div>
            )}

            <button type="submit" disabled={loading || (isRegister && (!consent.terms || !consent.location))} style={{ ...baseStyles.btn, ...baseStyles.btnPrimary, width: "100%", marginTop: 8, opacity: (loading || (isRegister && (!consent.terms || !consent.location))) ? 0.5 : 1 }}>
              {loading ? "處理中..." : isRegister ? "建立商業帳戶" : "登入"}
            </button>
          </form>

          <div style={{ textAlign: "center", marginTop: 20 }}>
            <span style={{ fontSize: 13, color: theme.textMuted }}>
              {isRegister ? "已有帳號？" : "還沒有帳號？"}
            </span>
            <button onClick={() => { setIsRegister(!isRegister); setError(""); setConsent({ terms: false, location: false, marketing: false }); }} style={{ background: "none", border: "none", color: theme.accent, fontSize: 13, cursor: "pointer", fontWeight: 600, marginLeft: 4 }}>
              {isRegister ? "登入" : "立即註冊"}
            </button>
          </div>
        </div>

        <div style={{ textAlign: "center", marginTop: 24, fontSize: 12, color: theme.textDim }}>
          © 2026 briolabs | TiKOUS, Inc.
          <div style={{ marginTop: 8 }}>
            <a href="https://legal.briolabs.io" target="_blank" rel="noopener noreferrer" style={{ color: theme.textDim, textDecoration: "none", borderBottom: `1px solid ${theme.textDim}44` }}>隱私權政策</a>
            <span style={{ margin: "0 6px" }}>・</span>
            <a href="https://legal.briolabs.io" target="_blank" rel="noopener noreferrer" style={{ color: theme.textDim, textDecoration: "none", borderBottom: `1px solid ${theme.textDim}44` }}>服務條款</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Persona Selection — Shown after registration when no capabilities exist
// ============================================================================
function PersonaSelection({ account, onCapabilityEnabled }) {
  const [enabling, setEnabling] = useState(null);
  const [toast, setToast] = useState(null);

  const personas = [
    { cap: "SCREEN_NETWORK", title: "我有場地", subtitle: "安裝螢幕、賺取廣告分潤", icon: "📺", color: theme.accent },
    { cap: "FLASH_BUY", title: "我有商品", subtitle: "上架商品、接受 Flash-Buy 訂單", icon: "🛒", color: theme.warning },
    { cap: "PARTNER", title: "我是經銷商", subtitle: "推廣安裝、賺取佣金", icon: "🔗", color: theme.success },
    { cap: "AD_BUYER", title: "我要投廣告", subtitle: "建立廣告活動、觸及目標受眾", icon: "📢", color: theme.diamond },
  ];

  const enableCapability = async (capType) => {
    setEnabling(capType);
    try {
      await api(`/capabilities/${capType}/enable`, {
        method: "POST",
        body: { terms_version: capType === "FLASH_BUY" ? "flash-buy-v1.1" : `${capType.toLowerCase()}-v1.0`, terms_accepted: true },
      });
      // Refresh account data
      const profile = await api("/profile");
      localStorage.setItem("biz_account", JSON.stringify(profile.data));
      onCapabilityEnabled(profile.data);
      setToast(`${CAPABILITY_META[capType]?.label || capType} 已開通！`);
    } catch (err) {
      setToast(err.message);
    }
    setEnabling(null);
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: "0 0 8px" }}>
          歡迎，{account.display_name} 👋
        </h1>
        <p style={{ color: theme.textMuted, fontSize: 15, margin: 0 }}>
          選擇您需要的功能模組（可隨時加開更多）
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {personas.map((p) => {
          const alreadyEnabled = hasCap(account, p.cap);
          return (
            <div
              key={p.cap}
              onClick={() => !alreadyEnabled && !enabling && enableCapability(p.cap)}
              style={{
                ...baseStyles.card,
                cursor: alreadyEnabled ? "default" : "pointer",
                borderColor: alreadyEnabled ? theme.success : theme.border,
                opacity: alreadyEnabled ? 0.6 : 1,
                transition: "all 0.2s",
                textAlign: "center",
                padding: 32,
              }}
            >
              <div style={{ fontSize: 48, marginBottom: 12 }}>{p.icon}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: p.color, marginBottom: 4 }}>{p.title}</div>
              <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 16 }}>{p.subtitle}</div>
              {alreadyEnabled ? (
                <span style={{ fontSize: 12, color: theme.success, fontWeight: 600 }}>✅ 已開通</span>
              ) : (
                <button
                  disabled={!!enabling}
                  style={{
                    ...baseStyles.btn,
                    ...baseStyles.btnPrimary,
                    opacity: enabling === p.cap ? 0.6 : 1,
                    fontSize: 13,
                    padding: "8px 20px",
                  }}
                >
                  {enabling === p.cap ? "開通中..." : "立即開通"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div style={{ textAlign: "center", marginTop: 24 }}>
        <button
          onClick={() => onCapabilityEnabled(account)}
          style={{ ...baseStyles.btn, ...baseStyles.btnOutline, fontSize: 13 }}
        >
          稍後再說，先進入後台 →
        </button>
      </div>
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// Dashboard Page — Capability-aware content
// ============================================================================
export function DashboardPage({ account }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api("/dashboard").then((r) => { setData(r.data); setLoading(false); }).catch((err) => {
      // e.g. 503 DASHBOARD_UNAVAILABLE: show "—" rather than counts of 0.
      console.error("[Dashboard] Load failed:", err);
      setData({ stats: null, codes: null, trend: [] });
      setLoadError(true);
      setLoading(false);
    });
  }, []);

  if (loading) return <Loading />;
  if (!data) return <div style={{ color: theme.textMuted, padding: 40 }}>無法載入</div>;
  const deviceStats = formatDeviceStats(data.stats);

  const caps = account.capabilities?.filter(c => c.status === "active") || [];
  const partnerConfig = getCapConfig(account, "PARTNER");
  const tier = TIERS[partnerConfig.tier] || TIERS.bronze;

  return (
    <div>
      {/* Welcome */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>
          歡迎回來，{account.display_name} 👋
        </h1>
        <p style={{ color: theme.textMuted, margin: "8px 0 0", fontSize: 15 }}>
          <span style={{ marginRight: 16 }}>📍 {account.legal_name || account.display_name}</span><span style={{ color: theme.textDim }}>⏰ {new Date().toLocaleDateString("zh-TW", { year: "numeric", month: "long", day: "numeric", weekday: "long" })}</span>
        </p>
      </div>

      {/* Active Capabilities */}
      <div style={{ ...baseStyles.card, background: `linear-gradient(135deg, ${theme.card}, #1E2235)` }}>
        <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 12 }}>已開通模組</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {caps.length === 0 ? (
            <div style={{ fontSize: 14, color: theme.textDim }}>尚未開通任何模組 — 前往「設定」開通功能</div>
          ) : caps.map((c) => {
            const meta = CAPABILITY_META[c.type];
            return (
              <div key={c.type} style={{ background: `${theme.accent}15`, border: `1px solid ${theme.accent}33`, borderRadius: 10, padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 20 }}>{meta?.icon}</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>{meta?.label || c.type}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Partner Tier Card — only if PARTNER capability */}
      {hasCap(account, "PARTNER") && (
        <div style={{ ...baseStyles.card, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ fontSize: 48 }}>{tier.emoji}</div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: tier.color }}>{tier.name}經銷商</div>
              <div style={{ color: theme.textMuted, fontSize: 14, marginTop: 4 }}>
                廣告分潤 {tier.share} · 安裝獎金 {tier.bonus}/台
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats — dynamically rendered based on API response */}
      {(data.stats || loadError) && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {hasCap(account, "SCREEN_NETWORK") && (
            <>
              <StatCard label="活躍設備" value={deviceStats.value} sub={deviceStats.sub} icon="📺" color={theme.success} />
              <StatCard label="本月新增" value={loadError ? UNAVAILABLE : data.stats.thisMonthInstalls || 0} icon="📈" color={theme.accent} />
            </>
          )}
          {hasCap(account, "PARTNER") && (
            <StatCard label="總安裝數" value={formatCount(loadError ? null : data.stats.totalInstalls)} icon="📦" />
          )}
          
            <StatCard label="預估佣金" value={formatMoney(loadError ? null : data.stats.estimatedCommission)} icon="💰" color={theme.warning} />
        </div>
      )}

      {/* Code Summary — only if PARTNER */}
      {hasCap(account, "PARTNER") && data.codes && (
        <div style={baseStyles.card}>
          <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600 }}>配對代碼總覽</h3>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            {[
              { label: "總計", value: data.codes.total, color: theme.text },
              { label: "未使用", value: data.codes.unclaimed, color: theme.accent },
              { label: "已配對", value: data.codes.claimed, color: theme.success },
              { label: "已過期", value: data.codes.expired, color: theme.textDim },
            ].map((s) => (
              <div key={s.label} style={{ textAlign: "center", minWidth: 70 }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 12, color: theme.textMuted }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Balance — Ad Revenue */}
      {(hasCap(account, "PARTNER") || hasCap(account, "SCREEN_NETWORK")) && data.stats && (() => {
        // null = balance could not be read: show "—", never NT$0.
        const balance = typeof data.stats.availableBalance === "number" ? data.stats.availableBalance : null;
        return (
        <div style={{ ...baseStyles.card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, color: theme.textMuted }}>廣告收益可提領餘額</div>
            <div style={{ fontSize: 32, fontWeight: 700, color: theme.success }}>{formatMoney(balance)}</div>
            <div style={{ fontSize: 12, color: theme.textDim, marginTop: 4 }}>最低提領 NT$1,000 · Flash-Buy 交易款由 PSP 自動撥付</div>
          </div>
          <button
            onClick={() => {
              if (balance === null) setToast("目前無法取得可提領餘額");
              else if (balance < 1000) setToast(`餘額不足，最低提領金額為 NT$1,000`);
            }}
            style={{ ...baseStyles.btn, ...baseStyles.btnPrimary, opacity: balance !== null && balance >= 1000 ? 1 : 0.4, position: "relative" }}
          >
            申請提領
            {(balance === null || balance < 1000) && <span style={{ display: "block", fontSize: 10, opacity: 0.7, marginTop: 2 }}>{balance === null ? "無法取得" : "未達門檻"}</span>}
          </button>
        </div>
        );
      })()}
      

      {/* 7-Day Revenue Trend */}
      {data.trend && data.trend.length > 0 && (
        <div style={{ ...baseStyles.card, padding: 20 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600 }}>📈 近 7 日營收趨勢</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.trend} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                <XAxis dataKey="date" stroke="#94A3B8" tick={{ fill: "#94A3B8", fontSize: 12 }} />
                <YAxis stroke="#94A3B8" tick={{ fill: "#94A3B8", fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: "#1E293B", borderColor: "#334155", borderRadius: 8, color: "#fff" }} itemStyle={{ color: "#22C55E" }} />
                <Line type="monotone" dataKey="daily" name="營收 (NT$)" stroke="#22C55E" strokeWidth={3} dot={{ r: 4, fill: "#22C55E", strokeWidth: 2, stroke: "#1E293B" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      

      {/* 7-Day Revenue Trend */}
      {data.trend && data.trend.length > 0 && (
        <div style={{ ...baseStyles.card, padding: 20 }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 600 }}>📈 近 7 日營收趨勢</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.trend} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D3748" vertical={false} />
                <XAxis dataKey="date" stroke="#94A3B8" tick={{ fill: "#94A3B8", fontSize: 12 }} />
                <YAxis stroke="#94A3B8" tick={{ fill: "#94A3B8", fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: "#1E293B", borderColor: "#334155", borderRadius: 8, color: "#fff" }} itemStyle={{ color: "#22C55E" }} />
                <Line type="monotone" dataKey="daily" name="營收 (NT$)" stroke="#22C55E" strokeWidth={3} dot={{ r: 4, fill: "#22C55E", strokeWidth: 2, stroke: "#1E293B" }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// Codes Page — Requires PARTNER capability
// ============================================================================
function CodesPage() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ count: 5, role: "clinic", note: "" });
  const [showForm, setShowForm] = useState(false);

  const roles = [
    { value: "clinic", label: "🏥 診所" },
    { value: "restaurant", label: "🍽️ 餐廳" },
    { value: "retail", label: "🛍️ 零售" },
    { value: "temple", label: "⛩️ 廟宇" },
    { value: "parking", label: "🅿️ 停車場" },
    { value: "office", label: "🏢 辦公大樓" },
    { value: "gym", label: "💪 健身房" },
    { value: "ev_charging", label: "⚡ EV充電站" },
    { value: "general", label: "📺 一般" },
  ];

  const fetchCodes = useCallback(async () => {
    try {
      const r = await api("/codes?limit=100");
      setCodes(r.data.codes);
    } catch (e) { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const generateCodes = async () => {
    setGenerating(true);
    try {
      await api("/codes/generate", { method: "POST", body: form });
      setShowForm(false);
      setForm({ count: 5, role: "clinic", note: "" });
      fetchCodes();
    } catch (e) { alert(e.message); }
    setGenerating(false);
  };

  const statusBadge = (status) => {
    const styles = {
      unclaimed: { bg: "#1A2332", color: theme.accent, label: "未使用" },
      claimed: { bg: "#1A2E1A", color: theme.success, label: "已配對" },
      expired: { bg: "#2A2225", color: theme.textDim, label: "已過期" },
    };
    const s = styles[status] || styles.unclaimed;
    return <span style={{ background: s.bg, color: s.color, padding: "4px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{s.label}</span>;
  };

  if (loading) return <Loading />;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>配對代碼管理</h1>
        <button onClick={() => setShowForm(!showForm)} style={{ ...baseStyles.btn, ...baseStyles.btnPrimary }}>
          ＋ 產生代碼
        </button>
      </div>

      {showForm && (
        <div style={{ ...baseStyles.card, borderColor: theme.accent }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 16 }}>批量產生配對代碼</h3>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
            <div style={{ flex: "1 1 100px" }}>
              <label style={baseStyles.label}>數量</label>
              <input style={baseStyles.input} type="number" min="1" max="100" value={form.count} onChange={(e) => setForm({ ...form, count: parseInt(e.target.value) || 1 })} />
            </div>
            <div style={{ flex: "2 1 200px" }}>
              <label style={baseStyles.label}>場景類型</label>
              <select style={{ ...baseStyles.input, cursor: "pointer" }} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                {roles.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div style={{ flex: "2 1 200px" }}>
              <label style={baseStyles.label}>備註</label>
              <input style={baseStyles.input} placeholder="例：板橋皮膚科" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={generateCodes} disabled={generating} style={{ ...baseStyles.btn, ...baseStyles.btnPrimary, opacity: generating ? 0.6 : 1 }}>
              {generating ? "產生中..." : `產生 ${form.count} 組代碼`}
            </button>
            <button onClick={() => setShowForm(false)} style={{ ...baseStyles.btn, ...baseStyles.btnOutline }}>取消</button>
          </div>
        </div>
      )}

      <div style={baseStyles.card}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                {["代碼", "場景", "備註", "狀態", "建立時間"].map((h) => (
                  <th key={h} style={{ textAlign: "left", padding: "12px 16px", color: theme.textMuted, fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {codes.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: "center", color: theme.textDim }}>尚無代碼，點擊上方按鈕產生</td></tr>
              ) : codes.map((c) => (
                <tr key={c.code} style={{ borderBottom: `1px solid ${theme.border}22` }}>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ fontFamily: "monospace", fontSize: 16, fontWeight: 700, color: theme.accentLight, letterSpacing: 2 }}>{c.code}</span>
                  </td>
                  <td style={{ padding: "12px 16px", color: theme.textMuted }}>{roles.find((r) => r.value === c.role)?.label || c.role}</td>
                  <td style={{ padding: "12px 16px", color: theme.textMuted }}>{c.partner_note || "—"}</td>
                  <td style={{ padding: "12px 16px" }}>{statusBadge(c.status)}</td>
                  <td style={{ padding: "12px 16px", color: theme.textDim, fontSize: 12 }}>{c.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Devices Page (設備管理) lives in DevicesPage.jsx — fleet status + venue-scoped
// add-device via ProvisioningPage's PairingCodeModal.

// ============================================================================
// Settings Page — Unified account settings + capability management
// ============================================================================
function SettingsPage({ account, onAccountUpdate, onLogout }) {
  const [bankLocked, setBankLocked] = useState(true);
  const [bankOtp, setBankOtp] = useState("");
  const [showBankOtp, setShowBankOtp] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingKyc, setSavingKyc] = useState(false);
  const [bankForm, setBankForm] = useState({ bankCode: "", bankAccount: "", bankAccountName: "" });
  const [kycForm, setKycForm] = useState({ taxId: "", idNumber: "" });
  const [kycFiles, setKycFiles] = useState({ idFront: null, idBack: null, bankbook: null });
  const [toast, setToast] = useState(null);
  const [capLoading, setCapLoading] = useState(null);

  useEffect(() => {
    api("/profile").then((r) => {
      setProfile(r.data);
      setBankForm({ bankCode: r.data.bank_code || "", bankAccount: r.data.bank_account || "", bankAccountName: r.data.bank_account_name || "" });
      setBankLocked(!!(r.data.bank_code && r.data.bank_account));
      setKycForm({ taxId: r.data.tax_id || "", idNumber: "" });
      setLoading(false);
    }).catch((err) => { console.error("[Revenue] Load failed:", err); setLoading(false); });
  }, []);

  const saveBank = async () => {
    setSaving(true);
    try {
      await api("/settings/bank-account", { method: "PUT", body: bankForm });
      setToast("銀行帳號已更新");
    } catch (e) { setToast(e.message); }
    setSaving(false);
  };

  const requestBankOtp = async () => {
    try {
      await api("/settings/bank-otp", { method: "POST" });
      setShowBankOtp(true);
      setToast("驗證碼已發送至您的 Email");
    } catch (e) { setToast(e.message || "發送驗證碼失敗"); }
  };

  const verifyBankOtp = async () => {
    if (bankOtp.length < 6) { setToast("請輸入 6 位驗證碼"); return; }
    try {
      await api("/settings/bank-otp/verify", { method: "POST", body: { code: bankOtp } });
      setBankLocked(false);
      setShowBankOtp(false);
      setBankOtp("");
      setToast("驗證成功，您可以修改銀行帳號");
    } catch (e) { setToast(e.message || "驗證碼錯誤"); }
  };

  const saveBankAndLock = async () => {
    await saveBank();
    setBankLocked(true);
    setShowBankOtp(false);
    setBankOtp("");
  };

  const saveKyc = async () => {
    if (!kycForm.taxId && !kycForm.idNumber) { setToast("請至少填寫統一編號或身分證字號"); return; }
    setSavingKyc(true);
    try {
      const formData = new FormData();
      formData.append("taxId", kycForm.taxId);
      formData.append("idNumber", kycForm.idNumber);
      if (kycFiles.idFront) formData.append("idFront", kycFiles.idFront);
      if (kycFiles.idBack) formData.append("idBack", kycFiles.idBack);
      if (kycFiles.bankbook) formData.append("bankbook", kycFiles.bankbook);
      await api("/settings/kyc", { method: "PUT", body: formData });
      setToast("實名認證資料已送出");
    } catch (e) { setToast(e.message); }
    setSavingKyc(false);
  };

  const handleFileSelect = (field) => (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setToast("檔案大小不得超過 5MB"); return; }
    setKycFiles({ ...kycFiles, [field]: file });
    setToast(`${file.name} 已選取（送出 KYC 時一併上傳）`);
  };

  const enableCapability = async (capType) => {
    setCapLoading(capType);
    try {
      await api(`/capabilities/${capType}/enable`, {
        method: "POST",
        body: { terms_version: capType === "FLASH_BUY" ? "flash-buy-v1.1" : `${capType.toLowerCase()}-v1.0`, terms_accepted: true },
      });
      const profileRes = await api("/profile");
      setProfile(profileRes.data);
      localStorage.setItem("biz_account", JSON.stringify(profileRes.data));
      onAccountUpdate(profileRes.data);
      setToast(`${CAPABILITY_META[capType]?.label} 已開通！`);
    } catch (err) { setToast(err.message); }
    setCapLoading(null);
  };

  if (loading) return <Loading />;

  const kycLevel = profile?.kyc_level || "L0";
  const kycBadge = {
    L0: { label: "未驗證", color: theme.textDim, bg: `${theme.textDim}22` },
    L1: { label: "基本驗證", color: theme.warning, bg: `${theme.warning}22` },
    L2: { label: "實名驗證", color: theme.accent, bg: `${theme.accent}22` },
    L3: { label: "金流核准", color: theme.success, bg: `${theme.success}22` },
  }[kycLevel] || { label: kycLevel, color: theme.textDim, bg: `${theme.textDim}22` };

  return (
    <div>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 24px" }}>設定</h1>

      {/* Profile */}
      <div style={baseStyles.card}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16 }}>商業帳戶資料</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={baseStyles.label}>帳戶 ID</label><div style={{ fontSize: 13, fontFamily: "monospace", color: theme.accentLight }}>{profile?.account_id}</div></div>
          <div><label style={baseStyles.label}>Email</label><div style={{ fontSize: 15 }}>{profile?.email}</div></div>
          <div><label style={baseStyles.label}>公司 / 商號</label><div style={{ fontSize: 15 }}>{profile?.display_name || "—"}</div></div>
          <div><label style={baseStyles.label}>聯絡人</label><div style={{ fontSize: 15 }}>{profile?.contact_name || "—"}</div></div>
          <div><label style={baseStyles.label}>手機</label><div style={{ fontSize: 15 }}>{profile?.phone || "—"}</div></div>
          <div>
            <label style={baseStyles.label}>KYC 等級</label>
            <span style={{ fontSize: 12, fontWeight: 600, color: kycBadge.color, background: kycBadge.bg, padding: "4px 10px", borderRadius: 6 }}>{kycBadge.label}</span>
          </div>
        </div>
      </div>

      {/* Capability Management */}
      <div style={baseStyles.card}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16 }}>功能模組管理</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {Object.entries(CAPABILITY_META).map(([type, meta]) => {
            const isActive = hasCap(account, type);
            const kycReq = { SCREEN_NETWORK: "L1", FLASH_BUY: "L3", AD_BUYER: "L1", PARTNER: "L1" }[type];
            const kycLevels = { L0: 0, L1: 1, L2: 2, L3: 3 };
            const canEnable = !isActive && (kycLevels[kycLevel] || 0) >= (kycLevels[kycReq] || 0);

            return (
              <div key={type} style={{
                padding: 16, borderRadius: 12,
                border: `1px solid ${isActive ? theme.success + "66" : theme.border}`,
                background: isActive ? `${theme.success}08` : theme.card,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 20 }}>{meta.icon}</span>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{meta.label}</span>
                  {isActive && <span style={{ fontSize: 11, color: theme.success, fontWeight: 600, marginLeft: "auto" }}>✅ 已開通</span>}
                </div>
                <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 12 }}>{meta.description}</div>
                {!isActive && (
                  <button
                    onClick={() => canEnable && enableCapability(type)}
                    disabled={!canEnable || !!capLoading}
                    style={{
                      ...baseStyles.btn, fontSize: 12, padding: "6px 14px",
                      ...(canEnable ? baseStyles.btnPrimary : { background: theme.border, color: theme.textDim, cursor: "not-allowed" }),
                      opacity: capLoading === type ? 0.6 : 1,
                    }}
                  >
                    {capLoading === type ? "開通中..." : canEnable ? "開通" : `需要 KYC ${kycReq}`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bank — OTP-protected */}
      <div style={baseStyles.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>銀行帳號 💳</h3>
          {bankLocked && bankForm.bankCode && (
            <span style={{ fontSize: 11, color: theme.success, display: "flex", alignItems: "center", gap: 4 }}>🔒 已鎖定</span>
          )}
        </div>

        {/* Masked view when locked */}
        {bankLocked && bankForm.bankCode ? (
          <div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              <div style={{ flex: "1 1 120px" }}>
                <label style={baseStyles.label}>銀行代碼</label>
                <div style={{ fontSize: 15, padding: "10px 0", color: theme.text }}>{bankForm.bankCode}</div>
              </div>
              <div style={{ flex: "2 1 200px" }}>
                <label style={baseStyles.label}>帳號</label>
                <div style={{ fontSize: 15, padding: "10px 0", color: theme.text, fontFamily: "monospace" }}>
                  {bankForm.bankAccount.length > 4 ? "••••••" + bankForm.bankAccount.slice(-4) : bankForm.bankAccount}
                </div>
              </div>
              <div style={{ flex: "1 1 150px" }}>
                <label style={baseStyles.label}>戶名</label>
                <div style={{ fontSize: 15, padding: "10px 0", color: theme.text }}>{bankForm.bankAccountName}</div>
              </div>
            </div>

            {/* OTP verification flow */}
            {!showBankOtp ? (
              <button onClick={requestBankOtp} style={{ ...baseStyles.btn, background: "transparent", border: `1.5px solid ${theme.accent}`, color: theme.accent, fontSize: 13 }}>
                🔓 修改銀行帳號
              </button>
            ) : (
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 200px" }}>
                  <label style={{ ...baseStyles.label, color: theme.accent }}>輸入 Email 驗證碼 (6 位)</label>
                  <input
                    style={{ ...baseStyles.input, borderColor: theme.accent, letterSpacing: 6, textAlign: "center", fontSize: 18, fontFamily: "monospace" }}
                    placeholder="000000"
                    maxLength={6}
                    value={bankOtp}
                    onChange={(e) => setBankOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    onKeyDown={(e) => e.key === "Enter" && bankOtp.length === 6 && verifyBankOtp()}
                    autoFocus
                  />
                </div>
                <button onClick={verifyBankOtp} disabled={bankOtp.length < 6} style={{ ...baseStyles.btn, ...baseStyles.btnPrimary, opacity: bankOtp.length >= 6 ? 1 : 0.4, flexShrink: 0 }}>
                  驗證解鎖
                </button>
                <button onClick={() => { setShowBankOtp(false); setBankOtp(""); }} style={{ ...baseStyles.btn, background: "transparent", border: `1px solid ${theme.border}`, color: theme.textMuted, flexShrink: 0 }}>
                  取消
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Editable view when unlocked or first time */
          <div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
              <div style={{ flex: "1 1 120px" }}>
                <label style={baseStyles.label}>銀行代碼</label>
                <input style={baseStyles.input} placeholder="812" value={bankForm.bankCode} onChange={(e) => setBankForm({ ...bankForm, bankCode: e.target.value })} />
              </div>
              <div style={{ flex: "2 1 200px" }}>
                <label style={baseStyles.label}>帳號</label>
                <input style={baseStyles.input} placeholder="00123456789" value={bankForm.bankAccount} onChange={(e) => setBankForm({ ...bankForm, bankAccount: e.target.value })} />
              </div>
              <div style={{ flex: "1 1 150px" }}>
                <label style={baseStyles.label}>戶名</label>
                <input style={baseStyles.input} placeholder="王大明" value={bankForm.bankAccountName} onChange={(e) => setBankForm({ ...bankForm, bankAccountName: e.target.value })} />
              </div>
            </div>
            <button onClick={bankLocked ? saveBank : saveBankAndLock} disabled={saving} style={{ ...baseStyles.btn, ...baseStyles.btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? "儲存中..." : "儲存銀行資訊"}
            </button>
          </div>
        )}
      </div>

      {/* KYC Section — Merchant KYC for Flash-Buy */}
      <div style={{ ...baseStyles.card, borderColor: kycLevel === "L3" ? theme.success : theme.border }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>🛡️ 實名認證 (KYC)</h3>
          <span style={{ fontSize: 12, fontWeight: 600, color: kycBadge.color, background: kycBadge.bg, padding: "4px 10px", borderRadius: 6 }}>
            {kycBadge.label}
          </span>
        </div>
        {hasCap(account, "FLASH_BUY") || (!hasCap(account, "FLASH_BUY") && kycLevel < "L3") ? (
          <div style={{ background: `${theme.warning}11`, border: `1px solid ${theme.warning}33`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: theme.warning, fontWeight: 600 }}>開通 Flash-Buy 交易需完成 KYC L3 認證（PSP 進件審核）。純螢幕聯播或經銷夥伴僅需 L1。</div>
          </div>
        ) : null}
        <div style={{ fontSize: 13, color: theme.textMuted, marginBottom: 16, lineHeight: 1.6 }}>
          您的資料將傳輸予綠界科技 (ECPay)、收單銀行及金融聯合徵信中心進行 KYC 實名認證。
          <span style={{ color: theme.accent, cursor: "pointer" }} onClick={() => window.open("https://legal.briolabs.io/#sec3", "_blank")}> 詳見隱私權政策 §3.3</span>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ flex: "1 1 200px" }}>
            <label style={baseStyles.label}>公司統一編號 / 個人營登編號</label>
            <input style={baseStyles.input} placeholder="12345678" value={kycForm.taxId} onChange={(e) => setKycForm({ ...kycForm, taxId: e.target.value })} />
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <label style={baseStyles.label}>負責人身分證字號</label>
            <input style={baseStyles.input} placeholder="A123456789" value={kycForm.idNumber} onChange={(e) => setKycForm({ ...kycForm, idNumber: e.target.value })} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
          {[
            { field: "idFront", label: "身分證正面", icon: "🪪" },
            { field: "idBack", label: "身分證反面", icon: "🔄" },
            { field: "bankbook", label: "存摺封面", icon: "📒" },
          ].map(({ field, label, icon }) => (
            <div key={field} style={{ flex: "1 1 160px" }}>
              <label style={baseStyles.label}>{label}</label>
              <label style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "16px 12px", background: "#141720", border: `1px dashed ${kycFiles[field] ? theme.success : theme.border}`,
                borderRadius: 10, cursor: "pointer", fontSize: 13, color: kycFiles[field] ? theme.success : theme.textMuted, transition: "all 0.2s"
              }}>
                <span>{icon}</span>
                {kycFiles[field] ? kycFiles[field].name.slice(0, 15) : "點擊上傳"}
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleFileSelect(field)} />
              </label>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 11, color: theme.textDim, marginBottom: 12 }}>
          支援 JPG/PNG，每張上限 5MB。資料僅用於 PSP 進件審核，brioCAST 不會另作他用。
        </div>

        <button onClick={saveKyc} disabled={savingKyc || kycLevel === "L3"} style={{ ...baseStyles.btn, ...baseStyles.btnPrimary, opacity: (savingKyc || kycLevel === "L3") ? 0.4 : 1 }}>
          {kycLevel === "L3" ? "✅ 已通過審核" : savingKyc ? "送出中..." : "送出 KYC 資料"}
        </button>
      </div>


      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}

// ============================================================================
// Sidebar — Capability-gated navigation
// ============================================================================

// ============================================================================
// Menu Management Page (FLASH_BUY) — CRUD Version
// ============================================================================
function MenuManagementPage({ account }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddCat, setShowAddCat] = useState(false);
  const [saving, setSaving] = useState(false);
  const venueId = account?.venue_id || "ven_fuduchin_cafe_001";
  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: "Bearer " + token };

  const loadMenu = useCallback(async () => {
    try {
      const res = await fetch("https://api.briolabs.io/api/menu/" + venueId);
      const data = await res.json();
      setCategories(data.categories || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [venueId]);

  useEffect(() => { loadMenu(); }, [loadMenu]);

  const addItem = async (form) => {
    setSaving(true);
    try {
      const res = await fetch("https://api.briolabs.io/api/menu/item", {
        method: "POST", headers,
        body: JSON.stringify({ venue_id: venueId, ...form }),
      });
      const data = await res.json();
      if (data.success) { setShowAddItem(false); loadMenu(); }
      else alert(data.error || "新增失敗");
    } catch (e) { alert("新增失敗: " + e.message); }
    setSaving(false);
  };

  const updateItem = async (id, updates) => {
    setSaving(true);
    try {
      const res = await fetch("https://api.briolabs.io/api/menu/item/" + id, {
        method: "PUT", headers,
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (data.success) { setEditingItem(null); loadMenu(); }
      else alert(data.error || "更新失敗");
    } catch (e) { alert("更新失敗: " + e.message); }
    setSaving(false);
  };

  const deleteItem = async (id, name) => {
    if (!window.confirm(`確定要刪除「${name}」嗎？`)) return;
    try {
      const res = await fetch("https://api.briolabs.io/api/menu/item/" + id, {
        method: "DELETE", headers,
      });
      const data = await res.json();
      if (data.success) loadMenu();
      else alert(data.error || "刪除失敗");
    } catch (e) { alert("刪除失敗: " + e.message); }
  };

  const toggleAvailability = async (item) => {
    await updateItem(item.id, { is_available: !item.is_available });
  };

  const togglePopular = async (item) => {
    await updateItem(item.id, { is_popular: !item.is_popular });
  };

  const addCategory = async (form) => {
    setSaving(true);
    try {
      const res = await fetch("https://api.briolabs.io/api/menu/category", {
        method: "POST", headers,
        body: JSON.stringify({ venue_id: venueId, ...form }),
      });
      const data = await res.json();
      if (data.success) { setShowAddCat(false); loadMenu(); }
      else alert(data.error || "新增失敗");
    } catch (e) { alert("新增失敗: " + e.message); }
    setSaving(false);
  };

  const allItems = categories.reduce((acc, c) => [...acc, ...c.items.map(i => ({ ...i, catName: c.name, catIcon: c.icon }))], []);

  // ── Styles ──
  const btnPrimary = { padding: "10px 20px", borderRadius: 8, border: "none", background: "#F59E0B", color: "#000", fontSize: 14, fontWeight: 700, cursor: "pointer" };
  const btnSecondary = { padding: "10px 20px", borderRadius: 8, border: "1px solid " + (theme.border || "#334155"), background: "transparent", color: theme.textMuted || "#94A3B8", fontSize: 14, cursor: "pointer" };
  const btnDanger = { padding: "6px 10px", borderRadius: 6, border: "none", background: "#7F1D1D", color: "#FCA5A5", fontSize: 12, cursor: "pointer" };
  const toggleBtn = (active, color) => ({ padding: "4px 10px", borderRadius: 6, border: "none", background: active ? color + "22" : "transparent", color: active ? color : (theme.textMuted || "#64748B"), fontSize: 11, fontWeight: 600, cursor: "pointer" });

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: theme.text }}>🍽️ 菜單管理</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: theme.textMuted }}>管理您的數位菜單品項與定價</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setShowAddCat(true)} style={{ ...btnSecondary, fontSize: 13 }}>📂 新增分類</button>
          <button onClick={() => setShowAddItem(true)} style={{ ...btnPrimary, fontSize: 13 }}>➕ 新增品項</button>
          <a href={"https://api.briolabs.io/liff/menu/?venue=" + venueId} target="_blank" rel="noopener noreferrer" style={{ padding: "8px 16px", background: "#F59E0B22", color: "#F59E0B", borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: "none", border: "1px solid #F59E0B44" }}>📱 預覽菜單</a>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[
          { label: "分類數", value: categories.length, icon: "📂" },
          { label: "品項數", value: allItems.length, icon: "🍴" },
          { label: "人氣品項", value: allItems.filter(i => i.is_popular).length, icon: "🔥" },
          { label: "已下架", value: allItems.filter(i => !i.is_available).length, icon: "⏸️" },
        ].map((s, i) => (
          <div key={i} style={{ ...baseStyles.card, textAlign: "center", padding: 16 }}>
            <div style={{ fontSize: 24 }}>{s.icon}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: "#F59E0B", marginTop: 4 }}>{s.value}</div>
            <div style={{ fontSize: 12, color: theme.textMuted }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Category + Items List */}
      {loading ? <div style={{ textAlign: "center", padding: 40, color: theme.textMuted }}>載入中...</div> : categories.map(cat => (
        <div key={cat.id} style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, color: theme.text, marginBottom: 10 }}>{cat.icon} {cat.name} ({cat.items.length})</h3>
          {cat.items.map(item => (
            editingItem === item.id ? (
              <EditItemRow key={item.id} item={item} categories={categories} saving={saving}
                onSave={(updates) => updateItem(item.id, updates)}
                onCancel={() => setEditingItem(null)} />
            ) : (
              <div key={item.id} style={{ ...baseStyles.card, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", marginBottom: 8, opacity: item.is_available === false ? 0.5 : 1 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                    {item.name}
                    {item.is_popular && <span style={{ fontSize: 10, background: "#EF4444", color: "#fff", padding: "1px 6px", borderRadius: 4, marginLeft: 6 }}>人氣</span>}
                    {item.is_available === false && <span style={{ fontSize: 10, background: "#6B7280", color: "#fff", padding: "1px 6px", borderRadius: 4, marginLeft: 6 }}>已下架</span>}
                  </div>
                  <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>{item.description}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ textAlign: "right", marginRight: 8 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: theme.success || "#22C55E" }}>NT${item.price}</div>
                    {item.original_price && <div style={{ fontSize: 12, color: theme.textMuted, textDecoration: "line-through" }}>NT${item.original_price}</div>}
                  </div>
                  <button onClick={() => togglePopular(item)} style={toggleBtn(item.is_popular, "#EF4444")} title="人氣">🔥</button>
                  <button onClick={() => toggleAvailability(item)} style={toggleBtn(item.is_available !== false, "#22C55E")} title="上架/下架">{item.is_available !== false ? "✅" : "⏸️"}</button>
                  <button onClick={() => setEditingItem(item.id)} style={{ ...btnSecondary, padding: "6px 10px", fontSize: 12 }}>✏️</button>
                  <button onClick={() => deleteItem(item.id, item.name)} style={btnDanger}>🗑️</button>
                </div>
              </div>
            )
          ))}
        </div>
      ))}

      {/* Add Item Modal */}
      {showAddItem && <AddItemModal categories={categories} saving={saving} onSave={addItem} onClose={() => setShowAddItem(false)} />}

      {/* Add Category Modal */}
      {showAddCat && <AddCategoryModal saving={saving} onSave={addCategory} onClose={() => setShowAddCat(false)} />}
    </div>
  );
}

// ── Inline Edit Row ──
function EditItemRow({ item, categories, saving, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: item.name || "", name_en: item.name_en || "", description: item.description || "",
    price: item.price || 0, original_price: item.original_price || "",
    category_id: item.category_id || "", is_popular: item.is_popular || false,
  });
  const inputStyle = { padding: "8px 10px", borderRadius: 6, border: "1px solid " + (theme.border || "#334155"), background: theme.inputBg || "#0F172A", color: theme.text || "#fff", fontSize: 13 };
  return (
    <div style={{ ...baseStyles.card, padding: 16, marginBottom: 8, border: "1px solid #F59E0B44" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="品名" />
        <input style={inputStyle} value={form.name_en} onChange={e => setForm({ ...form, name_en: e.target.value })} placeholder="English name" />
        <input style={inputStyle} type="number" value={form.price} onChange={e => setForm({ ...form, price: parseInt(e.target.value) || 0 })} placeholder="價格" />
        <input style={inputStyle} type="number" value={form.original_price} onChange={e => setForm({ ...form, original_price: parseInt(e.target.value) || "" })} placeholder="原價 (選填)" />
        <select style={{ ...inputStyle, gridColumn: "1/-1" }} value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
          <option value="">選擇分類</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
        </select>
        <input style={{ ...inputStyle, gridColumn: "1/-1" }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="描述" />
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid " + (theme.border || "#334155"), background: "transparent", color: theme.textMuted || "#94A3B8", fontSize: 13, cursor: "pointer" }}>取消</button>
        <button onClick={() => onSave(form)} disabled={saving} style={{ padding: "8px 16px", borderRadius: 6, border: "none", background: "#F59E0B", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer", opacity: saving ? 0.5 : 1 }}>{saving ? "儲存中..." : "💾 儲存"}</button>
      </div>
    </div>
  );
}

// ── Add Item Modal ──
function AddItemModal({ categories, saving, onSave, onClose }) {
  const [form, setForm] = useState({ category_id: categories[0]?.id || "", name: "", name_en: "", description: "", price: "", original_price: "", is_popular: false });
  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid " + (theme.border || "#334155"), background: theme.inputBg || "#0F172A", color: theme.text || "#fff", fontSize: 14, marginBottom: 10 };
  const handleSave = () => {
    if (!form.name || !form.price) { alert("品名和價格為必填"); return; }
    onSave({ ...form, price: parseInt(form.price), original_price: form.original_price ? parseInt(form.original_price) : null });
  };
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: theme.cardBg || "#1E293B", borderRadius: 16, padding: 24, width: 400, maxWidth: "90vw", border: "1px solid " + (theme.border || "#334155") }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 16px", fontSize: 18, color: "#F59E0B" }}>➕ 新增品項</h3>
        <select style={inputStyle} value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })}>
          <option value="">選擇分類</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
        </select>
        <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="品名 *" />
        <input style={inputStyle} value={form.name_en} onChange={e => setForm({ ...form, name_en: e.target.value })} placeholder="English name" />
        <div style={{ display: "flex", gap: 8 }}>
          <input style={{ ...inputStyle, flex: 1 }} type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="售價 *" />
          <input style={{ ...inputStyle, flex: 1 }} type="number" value={form.original_price} onChange={e => setForm({ ...form, original_price: e.target.value })} placeholder="原價 (選填)" />
        </div>
        <input style={inputStyle} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="描述" />
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, cursor: "pointer", color: theme.text || "#fff", fontSize: 14 }}>
          <input type="checkbox" checked={form.is_popular} onChange={e => setForm({ ...form, is_popular: e.target.checked })} /> 🔥 標記為人氣品項
        </label>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid " + (theme.border || "#334155"), background: "transparent", color: theme.textMuted || "#94A3B8", fontSize: 14, cursor: "pointer" }}>取消</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#F59E0B", color: "#000", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: saving ? 0.5 : 1 }}>{saving ? "儲存中..." : "新增品項"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Add Category Modal ──
function AddCategoryModal({ saving, onSave, onClose }) {
  const [form, setForm] = useState({ name: "", name_en: "", icon: "🍽️", sort_order: 0 });
  const inputStyle = { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid " + (theme.border || "#334155"), background: theme.inputBg || "#0F172A", color: theme.text || "#fff", fontSize: 14, marginBottom: 10 };
  const icons = ["🍽️", "☕", "🥤", "🍰", "🍜", "🍕", "🥗", "🍔", "🍣", "🍺"];
  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: theme.cardBg || "#1E293B", borderRadius: 16, padding: 24, width: 400, maxWidth: "90vw", border: "1px solid " + (theme.border || "#334155") }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: "0 0 16px", fontSize: 18, color: "#F59E0B" }}>📂 新增分類</h3>
        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {icons.map(ic => (
            <button key={ic} onClick={() => setForm({ ...form, icon: ic })} style={{ width: 40, height: 40, borderRadius: 8, border: form.icon === ic ? "2px solid #F59E0B" : "1px solid " + (theme.border || "#334155"), background: form.icon === ic ? "#F59E0B22" : "transparent", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{ic}</button>
          ))}
        </div>
        <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="分類名稱 *" />
        <input style={inputStyle} value={form.name_en} onChange={e => setForm({ ...form, name_en: e.target.value })} placeholder="English name" />
        <input style={inputStyle} type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} placeholder="排序 (0=最前)" />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid " + (theme.border || "#334155"), background: "transparent", color: theme.textMuted || "#94A3B8", fontSize: 14, cursor: "pointer" }}>取消</button>
          <button onClick={() => { if (!form.name) { alert("名稱為必填"); return; } onSave(form); }} disabled={saving} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "#F59E0B", color: "#000", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: saving ? 0.5 : 1 }}>{saving ? "儲存中..." : "新增分類"}</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CMS Content Management Page (SCREEN_NETWORK)
// ============================================================================
function CMSPage({ account }) {
  const [templates, setTemplates] = useState([]);
  const [content, setContent] = useState([]);
  const [activeTab, setActiveTab] = useState("templates");
  const [loading, setLoading] = useState(true);
  useEffect(() => { loadCMS(); }, []);
  const loadCMS = async () => {
    try {
      const token = localStorage.getItem("token");
      const h = { Authorization: "Bearer " + token };
      const [tRes, cRes] = await Promise.all([
        fetch("https://api.briolabs.io/api/cms/templates", { headers: h }).then(r => r.json()),
        fetch("https://api.briolabs.io/api/cms/content", { headers: h }).then(r => r.json()),
      ]);
      setTemplates(Array.isArray(tRes.templates) ? tRes.templates : []);
      setContent(Array.isArray(cRes.content) ? cRes.content : []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  const tabStyle = (t) => ({ padding: "8px 20px", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer", background: activeTab === t ? "#F59E0B" : "transparent", color: activeTab === t ? "#000" : theme.textMuted, border: activeTab === t ? "none" : "1px solid " + theme.border });
  return (
    <div>
      <h2 style={{ margin: "0 0 4px", fontSize: 20, color: theme.text }}>📋 內容管理</h2>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.textMuted }}>管理場域模板、播放清單與上傳內容</p>
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <div style={tabStyle("templates")} onClick={() => setActiveTab("templates")}>📐 場景模板</div>
        <div style={tabStyle("content")} onClick={() => setActiveTab("content")}>📁 已上傳內容</div>
      </div>
      {loading ? <div style={{ textAlign: "center", padding: 40, color: theme.textMuted }}>載入中...</div> : activeTab === "templates" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {templates.map(t => (
            <div key={t.id} style={{ ...baseStyles.card, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}><div><div style={{ fontSize: 15, fontWeight: 700, color: theme.text }}>{t.name_zh || t.name}</div><div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>{t.name_en || ""}</div></div><span style={{ fontSize: 10, background: "#F59E0B22", color: "#F59E0B", padding: "2px 8px", borderRadius: 4, fontWeight: 600, height: "fit-content" }}>{t.category}</span></div>
              <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 8, lineHeight: 1.5 }}>{t.description || ""}</div>
              <div style={{ display: "flex", gap: 12, marginTop: 12, fontSize: 11, color: theme.textDim }}>
                <span>📐 {t.layout || "landscape"}</span><span>⏱️ {t.loop_duration || 60}s</span><span>📺 {t.ad_slots || 2} 廣告位</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        content.length === 0 ? (
          <div style={{ ...baseStyles.card, textAlign: "center", padding: 40 }}><div style={{ fontSize: 40, marginBottom: 12 }}>📁</div><div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>尚無上傳內容</div><div style={{ fontSize: 13, color: theme.textMuted }}>上傳圖片或影片至場域播放清單</div></div>
        ) : content.map(c => (
          <div key={c.id} style={{ ...baseStyles.card, display: "flex", justifyContent: "space-between", padding: "12px 16px", marginBottom: 8 }}><div><div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{c.original_name || c.filename}</div><div style={{ fontSize: 12, color: theme.textMuted }}>{c.content_type}</div></div><span style={{ fontSize: 10, background: theme.success + "22", color: theme.success, padding: "2px 8px", borderRadius: 4 }}>{c.status}</span></div>
        ))
      )}
    </div>
  );
}

// ============================================================================
// Queue Management Page (SCREEN_NETWORK)
// ============================================================================
// eslint-disable-next-line no-unused-vars
function QueueManagementPage({ account }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queueMode, setQueueMode] = useState("numbered");
  const [savingMode, setSavingMode] = useState(false);
  const venueId = account?.venue_id || "ven_fuduchin_cafe_001";
  const authHeaders = () => ({ "Content-Type": "application/json", Authorization: "Bearer " + (localStorage.getItem("biz_token") || "") });
  // 店家跑馬燈 / Bottom Ticker (venue-owned MARQUEE announcements)
  const MARQUEE_MAX = 50;
  const [marquees, setMarquees] = useState([]);
  const [marqueeText, setMarqueeText] = useState("");
  const [marqueeLoading, setMarqueeLoading] = useState(true);
  const [marqueeError, setMarqueeError] = useState("");
  const [marqueeSaving, setMarqueeSaving] = useState(false);
  const marqueeErrMsg = (r, d) => (r.status === 403 ? "無權限管理此場域" : (d && (d.error || d.message)) || ("操作失敗 (" + r.status + ")"));
  const loadMarquees = useCallback(async () => {
    setMarqueeLoading(true);
    try {
      const r = await fetch("https://api.briolabs.io/api/venues/" + venueId + "/announcements?status=active", { headers: authHeaders() });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setMarqueeError(marqueeErrMsg(r, d)); setMarquees([]); }
      else {
        setMarqueeError("");
        const list = (d.announcements || []).filter(a => a.type === "MARQUEE")
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setMarquees(list);
      }
    } catch (e) { setMarqueeError("無法連線，請稍後再試"); }
    setMarqueeLoading(false);
  }, [venueId]);
  useEffect(() => { loadMarquees(); }, [loadMarquees]);
  const addMarquee = async () => {
    const content = marqueeText.trim();
    if (!content || content.length > MARQUEE_MAX) return;
    setMarqueeSaving(true);
    try {
      const r = await fetch("https://api.briolabs.io/api/venues/" + venueId + "/announcements", { method: "POST", headers: authHeaders(), body: JSON.stringify({ type: "MARQUEE", content, priority: 0 }) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setMarqueeError(marqueeErrMsg(r, d)); }
      else { setMarqueeText(""); setMarqueeError(""); await loadMarquees(); }
    } catch (e) { setMarqueeError("無法連線，請稍後再試"); }
    setMarqueeSaving(false);
  };
  const deleteMarquee = async (id) => {
    setMarqueeSaving(true);
    try {
      const r = await fetch("https://api.briolabs.io/api/venues/" + venueId + "/announcements/" + id, { method: "DELETE", headers: authHeaders() });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setMarqueeError(marqueeErrMsg(r, d)); }
      else { setMarqueeError(""); await loadMarquees(); }
    } catch (e) { setMarqueeError("無法連線，請稍後再試"); }
    setMarqueeSaving(false);
  };
  const marqueeRemaining = MARQUEE_MAX - marqueeText.length;
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("https://api.briolabs.io/api/venues/" + venueId + "/queue-mode", { headers: authHeaders() });
        if (r.ok) { const d = await r.json(); if (d.queue_mode) setQueueMode(d.queue_mode); }
      } catch (e) { /* keep default */ }
    })();
  }, [venueId]);
  const saveQueueMode = async (mode) => {
    setSavingMode(true);
    const prev = queueMode;
    setQueueMode(mode);
    try {
      const r = await fetch("https://api.briolabs.io/api/venues/" + venueId + "/queue-mode", { method: "PATCH", headers: authHeaders(), body: JSON.stringify({ queue_mode: mode }) });
      if (!r.ok) throw new Error("save failed");
    } catch (e) { setQueueMode(prev); alert("儲存失敗，請重試"); }
    setSavingMode(false);
  };
  const QUEUE_MODES = [
    { id: "numbered", label: "抽號制", desc: "顯示叫號 / 等候組數 / 取號 QR" },
    { id: "walk_in", label: "開放進場", desc: "歡迎入座 · 菜單 / LINE QR，不顯示叫號" },
    { id: "off", label: "關閉佇列", desc: "不顯示右側叫號面板" },
  ];
  const loadQueue = useCallback(async () => {
    try {
      const res = await fetch("https://api.briolabs.io/api/queue/tickets/" + venueId + "/active");
      const data = await res.json();
      setTickets(data.tickets || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [venueId]);
  useEffect(() => { loadQueue(); const iv = setInterval(loadQueue, 5000); return () => clearInterval(iv); }, [loadQueue]);
  const callNext = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch("https://api.briolabs.io/api/queue/call-next", { method: "POST", headers: { "Content-Type": "application/json", Authorization: "Bearer " + token }, body: JSON.stringify({ venue_id: venueId, counter_id: "1" }) });
      loadQueue();
    } catch (e) { alert("呼叫失敗"); }
  };
  const waiting = tickets.filter(t => t.status === "waiting");
  const called = tickets.filter(t => t.status === "called");
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div><h2 style={{ margin: 0, fontSize: 20, color: theme.text }}>🎫 叫號管理</h2><p style={{ margin: "4px 0 0", fontSize: 13, color: theme.textMuted }}>即時管理場域叫號系統</p></div>
        <div style={{ display: "flex", gap: 8 }}>
          <a href={"https://api.briolabs.io/queue/staff/?venue=" + venueId} target="_blank" rel="noopener noreferrer" style={{ padding: "8px 16px", background: "#22C55E22", color: "#22C55E", borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: "none", border: "1px solid #22C55E44" }}>🖥️ 全螢幕 KDS</a>
          <a href={"https://api.briolabs.io/liff/queue/?venue=" + venueId} target="_blank" rel="noopener noreferrer" style={{ padding: "8px 16px", background: "#F59E0B22", color: "#F59E0B", borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: "none", border: "1px solid #F59E0B44" }}>📱 客戶取號</a>
        </div>
      </div>
      <div style={{ ...baseStyles.card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, marginBottom: 4 }}>抽號模式 · Queue Mode</div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 14 }}>設定此場域螢幕右側面板的顯示方式</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {QUEUE_MODES.map(m => (
            <button key={m.id} onClick={() => saveQueueMode(m.id)} disabled={savingMode}
              style={{ textAlign: "left", padding: 14, borderRadius: 12, cursor: savingMode ? "wait" : "pointer",
                background: queueMode === m.id ? theme.accent + "22" : "#141720",
                border: "1px solid " + (queueMode === m.id ? theme.accent : theme.border), color: theme.text }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: queueMode === m.id ? theme.accent : theme.text }}>{m.label}</div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4, lineHeight: 1.4 }}>{m.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div style={{ ...baseStyles.card, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.text, marginBottom: 4 }}>📢 店家跑馬燈</div>
        <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 14 }}>顯示在螢幕底部，用於店家促銷、提醒與現場訊息。</div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <input
              type="text"
              value={marqueeText}
              maxLength={MARQUEE_MAX}
              onChange={e => setMarqueeText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && marqueeText.trim() && marqueeText.length <= MARQUEE_MAX && !marqueeSaving) addMarquee(); }}
              placeholder="例如：今日拿鐵 NT$120 限量供應"
              style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8, background: "#141720", border: "1px solid " + theme.border, color: theme.text, fontSize: 14 }}
            />
            <div style={{ fontSize: 11, color: marqueeRemaining < 0 ? "#EF4444" : theme.textMuted, marginTop: 6 }}>
              剩餘 {marqueeRemaining} 字 · 最多 50 字，會顯示在底部跑馬燈。重大訊息仍由上方即時資訊列顯示。
            </div>
          </div>
          <button
            onClick={addMarquee}
            disabled={!marqueeText.trim() || marqueeText.length > MARQUEE_MAX || marqueeSaving}
            style={{ padding: "10px 16px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap",
              background: (!marqueeText.trim() || marqueeText.length > MARQUEE_MAX || marqueeSaving) ? theme.border : "linear-gradient(135deg, #F59E0B, #D97706)",
              color: (!marqueeText.trim() || marqueeText.length > MARQUEE_MAX || marqueeSaving) ? theme.textMuted : "#000",
              cursor: (!marqueeText.trim() || marqueeText.length > MARQUEE_MAX || marqueeSaving) ? "not-allowed" : "pointer" }}>
            新增跑馬燈
          </button>
        </div>
        {marqueeError && <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, background: "#EF444422", border: "1px solid #EF444444", color: "#EF4444", fontSize: 12 }}>{marqueeError}</div>}
        <div style={{ marginTop: 14 }}>
          {marqueeLoading ? (
            <div style={{ textAlign: "center", padding: 20, color: theme.textMuted, fontSize: 13 }}>載入中...</div>
          ) : marquees.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: theme.textMuted, fontSize: 13, border: "1px dashed " + theme.border, borderRadius: 8 }}>尚無店家跑馬燈訊息</div>
          ) : marquees.map(m => (
            <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 14px", marginBottom: 6, borderRadius: 8, background: "#141720", border: "1px solid " + theme.border }}>
              <div style={{ fontSize: 14, color: theme.text, wordBreak: "break-word" }}>{m.content}</div>
              <button onClick={() => deleteMarquee(m.id)} disabled={marqueeSaving} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid #EF444444", background: "#EF444422", color: "#EF4444", fontSize: 12, fontWeight: 600, cursor: marqueeSaving ? "wait" : "pointer", whiteSpace: "nowrap" }}>刪除</button>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
        {[{ label: "等候中", value: waiting.length, color: "#F59E0B" }, { label: "已叫號", value: called.length, color: "#22C55E" }, { label: "目前叫號", value: called.length > 0 ? called[called.length-1].display_number : "--", color: "#EAB308" }, { label: "LINE 取號", value: tickets.filter(t => t.source === "line").length, color: "#06C755" }].map((s, i) => (
          <div key={i} style={{ ...baseStyles.card, textAlign: "center", padding: 16 }}><div style={{ fontSize: 28, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div><div style={{ fontSize: 12, color: theme.textMuted }}>{s.label}</div></div>
        ))}
      </div>
      <button onClick={callNext} disabled={waiting.length === 0} style={{ width: "100%", padding: 16, borderRadius: 12, border: "none", fontSize: 18, fontWeight: 800, background: waiting.length > 0 ? "linear-gradient(135deg, #F59E0B, #D97706)" : theme.border, color: waiting.length > 0 ? "#000" : theme.textMuted, cursor: waiting.length > 0 ? "pointer" : "not-allowed", marginBottom: 24 }}>📢 呼叫下一位</button>
      {loading ? <div style={{ textAlign: "center", padding: 40, color: theme.textMuted }}>載入中...</div> : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div><h3 style={{ fontSize: 14, color: "#22C55E", marginBottom: 10 }}>🔔 已叫號 ({called.length})</h3>{called.length === 0 ? <div style={{ ...baseStyles.card, padding: 20, textAlign: "center", color: theme.textMuted }}>尚無叫號</div> : called.map(t => (<div key={t.id} style={{ ...baseStyles.card, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", borderColor: "#22C55E44" }}><div style={{ fontSize: 18, fontWeight: 800, color: "#22C55E" }}>{t.display_number}</div><div style={{ fontSize: 11, color: theme.textMuted }}>{t.counter_name || "櫃台"}</div></div>))}</div>
          <div><h3 style={{ fontSize: 14, color: "#F59E0B", marginBottom: 10 }}>⏳ 等候中 ({waiting.length})</h3>{waiting.length === 0 ? <div style={{ ...baseStyles.card, padding: 20, textAlign: "center", color: theme.textMuted }}>無人等候</div> : waiting.map(t => (<div key={t.id} style={{ ...baseStyles.card, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between" }}><div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>{t.display_number}</div><div style={{ fontSize: 11, color: theme.textMuted }}>👥 {t.party_size || 1}</div></div>))}</div>
        </div>
      )}
    </div>
  );
}

function Sidebar({ active, onNav, account, onLogout }) {
  const [showMenu, setShowMenu] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const GROUPS = [
    { key: "ops",       label: "場域與設備" },
    { key: "deviceops", label: "DeviceOps" },
    { key: "catalog",   label: "商品與行銷" },
    { key: "revenue", label: "收益與結算" },
    { key: "ads",     label: "廣告投放" },
    { key: "risk",    label: "風險與治理" },
    { key: "account", label: "帳戶" },
  ];

  const items = [
    { id: "dashboard",        label: "儀表板",  icon: "📊", always: true,                                         group: "ops" },
    { id: "devices",          label: "設備管理", icon: "📺", cap: "SCREEN_NETWORK",                               group: "ops" },
    { id: "cms",              label: "內容管理", icon: "📋", cap: "SCREEN_NETWORK",                               group: "ops" },
    { id: "venue-creatives",  label: "場域素材", icon: "🎬", cap: "SCREEN_NETWORK",                               group: "ops" },
    { id: "pilot-structure", label: "場域結構", icon: "🧭", always: true,                                        group: "ops" },
    { id: "provisioning",     label: "場域佈建", icon: "🏗️", cap: "SCREEN_NETWORK",                               group: "ops" },
    { id: "device-fleet",     label: "機隊管理", icon: "📺", cap: "SCREEN_NETWORK",                               group: "ops" },
    { id: "queue-mgmt",       label: "叫號管理", icon: "🎫", cap: "SCREEN_NETWORK",                               group: "ops" },
    { id: "deviceops",        label: "設備運維", icon: "🖥️", cap: "SCREEN_NETWORK",                               group: "deviceops" },
    { id: "line-binding",     label: "LINE 通知綁定", icon: "💬", cap: "SCREEN_NETWORK",                               group: "deviceops" },
    { id: "menu-mgmt",        label: "菜單管理", icon: "🍽️", cap: "FLASH_BUY",                                   group: "catalog" },
    { id: "flash-deals",      label: "限時優惠", icon: "🔥", cap: "FLASH_BUY",                                    group: "catalog" },
    { id: "marketing",        label: "行銷綁定", icon: "🔗", cap: "SCREEN_NETWORK",                               group: "catalog" },
    { id: "revenue",          label: "收益中心", icon: "💰", caps: ["PARTNER", "SCREEN_NETWORK", "FLASH_BUY"],    group: "revenue" },
    { id: "revenue-analytics",label: "營收分析", icon: "📈", always: true,                                        group: "revenue" },
    { id: "orders",           label: "訂單對帳", icon: "📋", cap: "FLASH_BUY",                                    group: "revenue" },
    { id: "ad-dashboard",     label: "廣告投放", icon: "📢", cap: "AD_BUYER",                                     group: "ads" },
    { id: "ad-campaigns",     label: "廣告活動", icon: "📋", cap: "AD_BUYER",                                     group: "ads" },
    { id: "disputes",         label: "爭議管理", icon: "🛡️", cap: "FLASH_BUY",                                   group: "risk" },
    { id: "ops-disputes",     label: "Ops 爭議", icon: "⚡", admin: true,                                         group: "risk" },
    { id: "alert-console",    label: "警示控制台", icon: "🚨", admin: true,                                         group: "risk" },
    { id: "codes",            label: "配對代碼", icon: "🔑", cap: "PARTNER",                                      group: "account" },
    { id: "fleet",            label: "場域管理", icon: "🏢", cap: "PARTNER",                                      group: "account" },
    { id: "notifications",    label: "通知設定", icon: "🔔", always: true,                                        group: "account" },
    { id: "settings",         label: "設定",    icon: "⚙️", always: true,                                        group: "account" },
  ].filter(item => {
    if (item.always) return true;
    if (item.admin) return account.account_id === "PUB-ADMIN1";
    if (item.cap) return hasCap(account, item.cap);
    if (item.caps) return item.caps.some(c => hasCap(account, c));
    return false;
  });

  const toggleGroup = (key) => setCollapsed(prev => ({ ...prev, [key]: !prev[key] }));

  return (
    <div style={{ width: 240, background: theme.card, borderRight: `1px solid ${theme.border}`, display: "flex", flexDirection: "column", flexShrink: 0, height: "100vh", position: "sticky", top: 0 }}>
      {/* Logo */}
      <div style={{ padding: "24px 20px 20px", borderBottom: `1px solid ${theme.border}`, display: "flex", alignItems: "center", gap: 10 }}>
        <img src="/briolabs-mark.svg" alt="briolabs" style={{ width: 32, height: 32, objectFit: "contain", flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentLight})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>brioCAST</div>
          <div style={{ fontSize: 11, color: theme.textDim, letterSpacing: 2, marginTop: 2 }}>BUSINESS PORTAL</div>
        </div>
      </div>

      {/* Account trigger — separated from brand zone */}
      <div style={{ position: "relative" }}>
        <button onClick={() => setShowMenu(!showMenu)} style={{
          display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "14px 20px",
          border: "none", borderBottom: `1px solid ${theme.border}`, cursor: "pointer",
          background: showMenu ? `${theme.accent}11` : "transparent", transition: "background 0.15s",
        }}
          onMouseEnter={e => { if (!showMenu) e.currentTarget.style.background = `${theme.accent}08`; }}
          onMouseLeave={e => { if (!showMenu) e.currentTarget.style.background = "transparent"; }}
        >
          <img src="/briolabs-mark.svg" alt="briolabs" style={{ width: 32, height: 32, objectFit: "contain" }} />
          <div style={{ flex: 1, textAlign: "left" }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{account?.display_name}</div>
            <div style={{ fontSize: 11, color: theme.textDim }}>{account?.email}</div>
          </div>
          <span style={{ fontSize: 11, color: "#6B7280", transform: showMenu ? "rotate(180deg)" : "none", transition: "transform 0.2s", display: "flex", alignItems: "center" }}>▾</span>
        </button>

        {showMenu && (
          <div style={{
            position: "absolute", top: "100%", left: 10, right: 10, zIndex: 100,
            background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 10,
            boxShadow: "0 12px 40px rgba(0,0,0,0.25), 0 0 1px rgba(0,0,0,0.1)", overflow: "hidden", marginTop: 4,
          }}>
            <button onClick={() => { onNav("settings"); setShowMenu(false); }} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "12px 16px", borderRadius: 8, margin: "4px 0",
              border: "none", background: "transparent", color: "#1A1D27", fontSize: 13, cursor: "pointer", textAlign: "left",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = `${theme.accent}11`; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >帳號設定</button>
            <div style={{ height: 1, background: "#E5E7EB", margin: "0 12px" }} />
            <button onClick={onLogout} style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "12px 16px", borderRadius: 8, margin: "4px 0",
              border: "none", background: "transparent", color: "#EF4444", fontSize: 13, cursor: "pointer", textAlign: "left",
            }}
              onMouseEnter={e => { e.currentTarget.style.background = `${theme.danger}11`; e.currentTarget.style.color = theme.danger; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >登出</button>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "12px 10px", overflowY: "auto" }}>
        {GROUPS.map((group, gi) => {
          const groupItems = items.filter(item => item.group === group.key);
          if (groupItems.length === 0) return null;
          const isCollapsed = !!collapsed[group.key];
          return (
            <div key={group.key} style={{ marginBottom: gi < GROUPS.length - 1 ? 4 : 0 }}>
              <button
                onClick={() => toggleGroup(group.key)}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "16px 14px 6px",
                  border: "none", background: "transparent", cursor: "pointer",
                  fontSize: 11, fontWeight: 600, color: "#5A5E72",
                  letterSpacing: 1.5, textTransform: "uppercase", textAlign: "left",
                }}
              >
                {group.label}
                <span style={{ fontSize: 9, transform: isCollapsed ? "rotate(-90deg)" : "none", transition: "transform 0.2s" }}>▾</span>
              </button>
              {!isCollapsed && groupItems.map(item => (
                <button key={item.id} onClick={() => { onNav(item.id); setShowMenu(false); }} style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 14px",
                  border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14,
                  fontWeight: active === item.id ? 600 : 400,
                  background: active === item.id ? `${theme.accent}22` : "transparent",
                  color: active === item.id ? theme.accentLight : theme.textMuted,
                  marginBottom: 2, textAlign: "left", transition: "all 0.15s",
                }}>
                  <span style={{ fontSize: 18 }}>{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div style={{ padding: "12px 20px 16px", fontSize: 10, color: theme.textDim, lineHeight: 1.8 }}>
        © 2026 briolabs | TiKOUS, Inc.<br/>
        <a href="https://legal.briolabs.io/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: theme.textDim, textDecoration: "none" }}>隱私權政策</a>
        <span style={{ margin: "0 4px" }}>·</span>
        <a href="https://legal.briolabs.io/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: theme.textDim, textDecoration: "none" }}>服務條款</a>
      </div>
    </div>
  );
}

// ============================================================================
// Cookie Consent Banner (unchanged)
// ============================================================================
function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem("briocast_cookie_consent");
    if (!saved) setVisible(true);
  }, []);

  const save = (acceptAll) => {
    localStorage.setItem("briocast_cookie_consent", JSON.stringify({
      necessary: true, vast: true, heartbeat: true,
      analytics: acceptAll ? true : analytics,
      timestamp: new Date().toISOString()
    }));
    setVisible(false);
  };

  if (!visible) return null;

  const lockBadge = { padding: "4px 12px", borderRadius: 20, background: theme.border, color: theme.textMuted, fontSize: 11, fontWeight: 600 };
  const row = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: `1px solid ${theme.border}` };

  return (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9999, background: theme.card, borderTop: `1px solid ${theme.border}`, boxShadow: "0 -4px 24px rgba(0,0,0,0.4)" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 24px" }}>
        {!expanded ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, marginBottom: 4 }}>🍪 Cookie 與追蹤技術設定</div>
              <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.6 }}>我們使用 Cookie 及類似技術來確保平台運作、驗證廣告曝光及改善服務體驗。<a href="https://legal.briolabs.io#sec10" target="_blank" rel="noopener noreferrer" style={{ color: theme.accentLight, textDecoration: "none" }}>了解更多</a></div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <button onClick={() => setExpanded(true)} style={{ padding: "8px 16px", borderRadius: 8, border: `1px solid ${theme.border}`, background: "transparent", color: theme.text, fontSize: 13, cursor: "pointer" }}>自訂設定</button>
              <button onClick={() => save(true)} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: theme.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>全部接受</button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.text, marginBottom: 16 }}>🍪 Cookie 與追蹤技術偏好設定</div>
            <div style={row}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>🔒 必要性 Cookie</div>
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>登入狀態維持、安全驗證、CSRF 防護</div>
              </div>
              <div style={lockBadge}>永遠啟用</div>
            </div>
            <div style={row}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>📡 廣告成效追蹤（VAST / CPM）</div>
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>曝光次數統計、點擊追蹤、分潤計算依據</div>
              </div>
              <div style={lockBadge}>永遠啟用</div>
            </div>
            <div style={row}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>💓 裝置心跳偵測（Heartbeat）</div>
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>設備在線狀態監控、異常告警</div>
              </div>
              <div style={lockBadge}>永遠啟用</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>📊 分析與體驗優化</div>
                <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>匿名使用行為分析、服務改善、介面優化</div>
              </div>
              <div onClick={() => setAnalytics(!analytics)} style={{ width: 44, height: 24, borderRadius: 24, background: analytics ? theme.accent : theme.border, position: "relative", cursor: "pointer", transition: "0.3s" }}>
                <div style={{ position: "absolute", top: 3, left: analytics ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "0.3s" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16, paddingTop: 16, borderTop: `1px solid ${theme.border}` }}>
              <button onClick={() => save(false)} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: theme.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>儲存偏好設定</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main App — Capability-aware routing
// ============================================================================
export default function BusinessPortal() {
  const [account, setAccount] = useState(null);
  const [page, setPage] = useState("dashboard");
  const [initialized, setInitialized] = useState(false);
  const [showPersonaSelection, setShowPersonaSelection] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("biz_token");
    const saved = localStorage.getItem("biz_account");
    if (token && saved) {
      try {
        const acc = JSON.parse(saved);
        setAccount(acc);
        // Show persona selection if no capabilities
        if (!acc.capabilities || acc.capabilities.length === 0) {
          setShowPersonaSelection(true);
        }
      } catch (e) { /* */ }
    }
    setInitialized(true);
  }, []);

  const handleLogin = (acc) => {
    setAccount(acc);
    if (!acc.capabilities || acc.capabilities.length === 0) {
      setShowPersonaSelection(true);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("biz_token");
    localStorage.removeItem("biz_account");
    setAccount(null);
    setPage("dashboard");
    setShowPersonaSelection(false);
  };

  const handleAccountUpdate = (acc) => {
    setAccount(acc);
    localStorage.setItem("biz_account", JSON.stringify(acc));
    setShowPersonaSelection(false);
  };

  if (!initialized) return null;

  if (!account) {
    return <><LoginPage onLogin={handleLogin} /><CookieConsent /></>;
  }

  // Show persona selection for new accounts with no capabilities
  if (showPersonaSelection) {
    return (
      <div style={baseStyles.page}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Noto+Sans+TC:wght@400;500;700&display=swap" rel="stylesheet" />
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "60px 24px" }}>
          <PersonaSelection account={account} onCapabilityEnabled={handleAccountUpdate} />
        </div>
        <CookieConsent />
      </div>
    );
  }

  const renderPage = () => {
    switch (page) {
      case "codes": return hasCap(account, "PARTNER") ? <CodesPage /> : <NoCap cap="PARTNER" />;
      case "devices": return hasCap(account, "SCREEN_NETWORK") ? <DevicesPage /> : <NoCap cap="SCREEN_NETWORK" />;
      case "revenue": return <RevenueDetailPage account={account} />;
      case "revenue-analytics": return <RevenueDashboardPage />;
      case "fleet": return hasCap(account, "PARTNER") ? <FleetDashboard /> : null;
      case "ops-disputes": return account.account_id === "PUB-ADMIN1" ? <OpsDisputeConsolePage /> : null;
      case "alert-console": return account.account_id === "PUB-ADMIN1" ? <AdminAlertConsolePage /> : null;
      case "disputes": return hasCap(account, "FLASH_BUY") ? <MerchantDisputesPage /> : <NoCap cap="FLASH_BUY" />;
      case "menu-mgmt": return hasCap(account, "FLASH_BUY") ? <MenuManagementPage account={account} /> : <NoCap cap="FLASH_BUY" />;
      case "cms": return hasCap(account, "SCREEN_NETWORK") ? <CMSPage account={account} /> : <NoCap cap="SCREEN_NETWORK" />;
      case "queue-mgmt": return hasCap(account, "SCREEN_NETWORK") ? <QueueConsolePage account={account} onAuthExpired={handleLogout} /> : <NoCap cap="SCREEN_NETWORK" />;
      case "settings": return <SettingsPage account={account} onAccountUpdate={handleAccountUpdate} onLogout={handleLogout} />;
            case "marketing": return hasCap(account, "SCREEN_NETWORK") ? <MarketingPage account={account} /> : <NoCap cap="SCREEN_NETWORK" />;
      case "notifications": return <NotificationsPage account={account} />;
      case "flash-deals": return hasCap(account, "FLASH_BUY") ? <FlashDealsPage account={account} /> : <NoCap cap="FLASH_BUY" />;
      case "orders": return hasCap(account, "FLASH_BUY") ? <OrdersPage account={account} /> : <NoCap cap="FLASH_BUY" />;
      case "ad-dashboard": return hasCap(account, "AD_BUYER") ? <AdDashboardPage account={account} /> : <NoCap cap="AD_BUYER" />;
case "ad-campaigns": return hasCap(account, "AD_BUYER") ? <AdCampaignsPage account={account} /> : <NoCap cap="AD_BUYER" />;
case "venue-creatives": return hasCap(account, "SCREEN_NETWORK") ? <VenueCreativesPage account={account} /> : <NoCap cap="SCREEN_NETWORK" />;
case "pilot-structure": return <PilotStructurePage account={account} />;
case "deviceops": return hasCap(account, "SCREEN_NETWORK") ? <DeviceOpsPage account={account} /> : <NoCap cap="SCREEN_NETWORK" />;
case "line-binding": return hasCap(account, "SCREEN_NETWORK") ? <LineBindingPage account={account} /> : <NoCap cap="SCREEN_NETWORK" />;
case "provisioning": return hasCap(account, "SCREEN_NETWORK") ? <ProvisioningPage account={account} /> : <NoCap cap="SCREEN_NETWORK" />;
case "device-fleet": return hasCap(account, "SCREEN_NETWORK") ? <DeviceFleetPage /> : <NoCap cap="SCREEN_NETWORK" />;
default: return <DashboardPage account={account} />;
    }
  };

  return (
    <div style={{ ...baseStyles.page, display: "flex" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Noto+Sans+TC:wght@400;500;700&display=swap" rel="stylesheet" />
      <Sidebar active={page} onNav={setPage} account={account} onLogout={handleLogout} />
      <main style={{ flex: 1, padding: "32px 40px", overflowY: "auto", height: "100vh" }}>
        <div style={{ maxWidth: 960 }}>
          {renderPage()}
        </div>
      </main>
      <CookieConsent />
    </div>
  );
}

// Fallback for capability-gated pages
function NoCap({ cap }) {
  const meta = CAPABILITY_META[cap];
  return (
    <div style={{ padding: 60, textAlign: "center" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>{meta?.icon || "🔒"}</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>需要開通「{meta?.label || cap}」模組</h2>
      <p style={{ color: theme.textMuted, fontSize: 14 }}>前往「設定」頁面開通此功能模組。</p>
    </div>
  );
}
